import { useEffect, useRef } from 'react';
import { useShepherd } from 'react-shepherd';
import { StepOptions, TourOptions } from 'shepherd.js';
import { useLocation } from 'react-router';
import 'shepherd.js/dist/css/shepherd.css';
import './Onboarding.css';

import {
  markTourAsShown,
  tourSeenKey,
  shouldStartTour,
  defaultShowHandler,
  middleware,
} from './utilities';

/** Sumtele embeds this viewer in an iframe on another origin, where the parent
 * page runs its own report-page tour first. Starting on load would put both
 * overlays on screen at once, so when embedded we announce ourselves and wait
 * to be told to go. */
const READY_MESSAGE = 'sumtele:tour-ready';
const START_MESSAGE = 'sumtele:start-tour';

const Onboarding = ({
  tours = [],
}: {
  tours?: Array<{
    id: string;
    route: string;
    tourOptions: TourOptions;
    steps: StepOptions[];
  }>;
}) => {
  const Shepherd = useShepherd();
  const location = useLocation();
  // getCustomization can hand back a fresh array each render, so the effect
  // below can re-run for the same tour. Without this it would start twice.
  const startedRef = useRef(false);

  /**
   * Show the tour if it hasn't been shown yet based on the current route.
   * Constructs a tour instance and adds steps to it based on the matching tour.
   */
  useEffect(() => {
    if (!tours.length) {
      return;
    }

    const matchingTour = tours.find(tour => tour.route === location.pathname);
    if (!matchingTour) {
      return;
    }

    const tourInstance = new Shepherd.Tour({
      ...matchingTour.tourOptions,
      defaultStepOptions: {
        ...matchingTour.tourOptions?.defaultStepOptions,
        floatingUIOptions: matchingTour.tourOptions?.defaultStepOptions?.floatingUIOptions || {
          middleware,
        },
        when: {
          ...matchingTour.tourOptions?.defaultStepOptions?.when,
          show:
            matchingTour.tourOptions?.defaultStepOptions?.when?.show ||
            (() => defaultShowHandler(Shepherd)),
        },
      },
    });
    matchingTour.steps.forEach(step => tourInstance.addStep(step));

    const start = (userId?: string | number, force?: boolean) => {
      if (!shouldStartTour(matchingTour.id, { userId, force, alreadyStarted: startedRef.current })) {
        return;
      }
      startedRef.current = true;
      tourInstance.start();
      // Marked here rather than before the handshake, so a user who closed the
      // tab while waiting is not recorded as having seen it.
      markTourAsShown(tourSeenKey(matchingTour.id, userId));
    };

    if (window.self === window.top) {
      start();
      return;
    }

    // The parent frame is the only sender we act on. The message carries no
    // data, so this is about ignoring noise rather than protecting a secret.
    let parentOrigin: string;
    try {
      parentOrigin = new URL(document.referrer).origin;
    } catch (e) {
      parentOrigin = null;
    }

    const onMessage = (event: MessageEvent) => {
      if (parentOrigin && event.origin !== parentOrigin) {
        return;
      }
      if (event.data?.type !== START_MESSAGE) {
        return;
      }
      start(event.data.userID, event.data.force);
    };

    window.addEventListener('message', onMessage);
    // Posted on mount, not on iframe load: the parent cannot know when this
    // component is listening, so we tell it.
    window.parent.postMessage({ type: READY_MESSAGE }, parentOrigin || '*');

    return () => window.removeEventListener('message', onMessage);
  }, [Shepherd, tours, location.pathname]);

  return null;
};

export { Onboarding };
